import {
  ComponentPropsWithRef,
  ComponentPropsWithoutRef,
  ReactNode,
  useState
} from 'react';
import { MdClose as CloseIcon } from 'react-icons/md';
import { twMerge } from 'tailwind-merge';

import { L10Common } from 'helpers/l10n/L10nCommon';

import * as Dialog from '@radix-ui/react-dialog';
import AvomaTooltip from './AvomaTooltip';

interface IBaseAvomaDialogProps {
  trigger?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  showCancel?: boolean;
  closeCleanupHandler?: () => void;
  contentProps?: ComponentPropsWithRef<typeof Dialog.Content>;
  contentClasses?: string;
  headerClasses?: string;
  childrenClasses?: string;
  footerClasses?: string | false | null | undefined;
  titleClasses?: string;
  closeButtonClasses?: string;
  closeIconClasses?: string;
  portalContainer?: ComponentPropsWithoutRef<typeof Dialog.Portal>['container'];
}

type IAvomaDialogProps =
  | (IBaseAvomaDialogProps & {
      controlled?: false;
      open?: never;
      setOpen?: never;
    })
  | (IBaseAvomaDialogProps & {
      controlled: true;
      open: boolean;
      setOpen: (open: boolean) => void;
    });

const AvomaDialog = ({
  trigger,
  title,
  children,
  footer,
  controlled,
  showCancel = true,
  open,
  setOpen,
  closeCleanupHandler,
  contentProps,
  contentClasses,
  headerClasses,
  childrenClasses,
  footerClasses,
  titleClasses,
  closeButtonClasses,
  closeIconClasses,
  portalContainer = document.body
}: IAvomaDialogProps) => {
  const [internalPopoverState, setInternalPopoverState] = useState(false);
  const popoverState = controlled ? open : internalPopoverState;
  const setPopoverState = controlled ? setOpen : setInternalPopoverState;
  const handleCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    contentProps?.onCloseAutoFocus?.(event);
  };

  return (
    <Dialog.Root
      open={popoverState}
      modal
      onOpenChange={isOpen => {
        if (!isOpen) {
          if (typeof closeCleanupHandler === 'function') {
            closeCleanupHandler();
          }
          setPopoverState(false);
        } else {
          setPopoverState(true);
        }
      }}
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      {children && (
        <Dialog.Portal container={portalContainer}>
          <Dialog.Overlay className='fixed inset-0 z-[9999] bg-black/30 data-[state=open]:animate-overlayShow' />
          <Dialog.Content
            className={twMerge(
              'fixed top-[50%] left-[50%] z-[10000] max-h-[85vh] w-[90vw] max-w-[640px] translate-x-[-50%] translate-y-[-50%] rounded bg-white shadow-md focus:outline-none data-[state=open]:animate-contentShow',
              contentClasses
            )}
            {...contentProps}
            onCloseAutoFocus={handleCloseAutoFocus}
          >
            {title && (
              <section
                className={twMerge(
                  'flex items-center justify-between rounded-t bg-snow px-6 py-4',
                  headerClasses
                )}
              >
                <Dialog.Title
                  className={twMerge(
                    'm-0 mr-4 max-w-full w-full truncate text-base font-bold text-black',
                    titleClasses
                  )}
                >
                  {title}
                </Dialog.Title>
                <AvomaTooltip
                  tooltip={L10Common.close}
                  tooltipPosition='bottom-center'
                >
                  {showCancel && (
                    <Dialog.Close asChild>
                      <button
                        type='button'
                        className={twMerge(
                          'flex cursor-pointer appearance-none items-center justify-center rounded-md bg-transparent p-2 hover:bg-gainsboro focus:outline-none',
                          closeButtonClasses
                        )}
                        aria-label={L10Common.close}
                      >
                        <CloseIcon
                          className={twMerge(
                            'h-4 w-4 text-gunmetal',
                            closeIconClasses
                          )}
                        />
                      </button>
                    </Dialog.Close>
                  )}
                </AvomaTooltip>
              </section>
            )}

            <div
              className={twMerge(
                'max-h-[calc(85vh-12rem)] overflow-y-auto px-8 pt-6 pb-8',
                childrenClasses
              )}
            >
              {children}
            </div>

            {footer && (
              <section
                className={twMerge(
                  'flex items-center bg-white px-8 py-5',
                  footerClasses
                )}
              >
                {footer}
              </section>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      )}
    </Dialog.Root>
  );
};

export default AvomaDialog;
